import { fireBeacon } from './beacon.js';
import type { CompanionAd } from './types.js';

/**
 * Render a Companion ad inside the supplied slot. Returns the rendered
 * element (or null if nothing renderable). The first Companion that fits
 * the slot's dimensions wins; if none fit, the largest is used.
 *
 * Tracking: `creativeView` fires once on mount; `ClickTracking` URLs fire
 * on click (alongside opening `CompanionClickThrough`).
 */
export function renderCompanion(slot: HTMLElement, companions: CompanionAd[]): HTMLElement | null {
  if (companions.length === 0) return null;
  const slotW = slot.clientWidth || 300;
  const slotH = slot.clientHeight || 250;

  const fits = companions.filter((c) => c.width <= slotW && c.height <= slotH);
  const chosen = fits.length > 0
    ? fits.slice().sort((a, b) => b.width * b.height - a.width * a.height)[0]
    : companions.slice().sort((a, b) => b.width * b.height - a.width * a.height)[0];
  if (!chosen) return null;

  slot.innerHTML = '';
  let inner: HTMLElement;
  if (chosen.htmlContent) {
    inner = document.createElement('div');
    inner.innerHTML = chosen.htmlContent;
  } else if (chosen.type === 'iframe' && chosen.resourceUrl) {
    const iframe = document.createElement('iframe');
    iframe.src = chosen.resourceUrl;
    iframe.style.border = '0';
    iframe.width = String(chosen.width);
    iframe.height = String(chosen.height);
    iframe.referrerPolicy = 'no-referrer';
    iframe.loading = 'lazy';
    inner = iframe;
  } else if (chosen.resourceUrl) {
    const img = document.createElement('img');
    img.src = chosen.resourceUrl;
    img.width = chosen.width;
    img.height = chosen.height;
    img.alt = '';
    img.style.display = 'block';
    inner = img;
  } else {
    return null;
  }

  inner.style.maxWidth = '100%';

  // Click handling — wrap in <a> when there's a ClickThrough URL.
  if (chosen.clickThrough) {
    const a = document.createElement('a');
    a.href = chosen.clickThrough;
    a.target = '_blank';
    a.rel = 'noopener noreferrer sponsored';
    a.style.display = 'inline-block';
    a.addEventListener('click', () => {
      chosen.clickTracking.forEach(fireBeacon);
    });
    a.appendChild(inner);
    slot.appendChild(a);
  } else {
    slot.appendChild(inner);
  }

  // creativeView fires when the companion renders.
  chosen.trackingEvents
    .filter((t) => t.event === 'creativeView')
    .forEach((t) => fireBeacon(t.url));

  return slot.firstElementChild as HTMLElement;
}
